import React from 'react';
import { Reveal } from './Reveal';
import { ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { BRAND_BIRD_LOGO } from '../mock';

const ProudlyIndian = () => (
  <div className="lg:col-span-3" data-testid="footer-india-map">
    <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] mb-4 uppercase">
      Proudly Indian
    </div>
    <motion.div
      className="group relative w-[150px] lg:w-[170px]"
      initial={{ opacity: 0, y: 24, scale: 0.94 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="absolute inset-4 rounded-full bg-[#ff6c0a]/15 blur-2xl group-hover:bg-[#ff6c0a]/25 transition-colors duration-500" />
      <img
        src="/india-map.webp"
        alt="Map of India"
        className="relative w-full h-auto group-hover:scale-[1.04] transition-transform duration-500"
      />
    </motion.div>
    <div
      className="mt-4 text-[17px] italic font-light text-[#0f2c6b]"
      style={{ fontFamily: 'Fraunces, serif' }}
    >
      Born in India.
    </div>
  </div>
);

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-[#fafaf7] text-[#0f2c6b] overflow-hidden border-t border-slate-200">
      {/* Decorative shapes */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#c4a24a]/[0.07] blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-sky-200/30 blur-3xl" />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-16">
        {/* Top: Big brand block */}
        <Reveal>
          <div className="py-16 lg:py-20 border-b border-slate-200">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] mb-8 uppercase">
                  Every drop · Pure · Protected · Premium
                </div>
                {/* Bird logo — transparent PNG, clearly visible on the light footer */}
                <div className="relative inline-block">
                  <div className="absolute inset-0 rounded-full bg-[#c4a24a]/10 blur-3xl" />
                  <img
                    src={BRAND_BIRD_LOGO}
                    alt="Tropic Blue"
                    className="relative w-[240px] h-[240px] lg:w-[300px] lg:h-[300px] xl:w-[340px] xl:h-[340px] object-contain"
                  />
                </div>
              </div>
              <div className="lg:col-span-4 flex justify-start lg:justify-end">
                <button
                  onClick={scrollToTop}
                  className="group flex items-center gap-4 text-[11px] tracking-[0.22em] text-[#0f2c6b]/70 hover:text-[#c4a24a] transition-colors duration-300"
                >
                  <div className="w-12 h-12 rounded-full border border-[#0f2c6b]/25 group-hover:border-[#c4a24a] flex items-center justify-center transition-colors duration-300">
                    <ArrowUpRight size={16} className="-rotate-90 group-hover:rotate-0 transition-transform duration-500" />
                  </div>
                  BACK TO TOP
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Middle: Info grid */}
        <div className="py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 border-b border-slate-200">
          <div className="lg:col-span-4">
            <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] mb-4 uppercase">
              Legal Notice
            </div>
            <p className="text-[12px] text-slate-600 leading-[1.75] font-light max-w-md">
              Tropic Blue® is a registered trademark. Unauthorized use, reproduction, copying, imitation, manufacturing or distribution of our trademark, registered bottle design, labels, packaging artwork or associated intellectual property is strictly prohibited and may attract civil and criminal liability under applicable laws.
            </p>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] mb-4 uppercase">
              Explore
            </div>
            <ul className="space-y-2 text-[13px] text-[#0f2c6b]/80 font-light">
              {['Home', 'About Us', 'Products', 'Quality', 'Certifications', 'Sustainability', 'Contact'].map(
                (item) => (
                  <li key={item}>
                    <a
                      href={`#${item.toLowerCase().replace(' ', '')}`}
                      className="hover:text-[#c4a24a] link-underline transition-colors duration-300"
                    >
                      {item}
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>

          <ProudlyIndian />

          <div className="lg:col-span-3">
            <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] mb-4 uppercase">
              Bottle Trademarks Managed By
            </div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full border-2 border-[#0f2c6b]/70 flex items-center justify-center">
                <span
                  className="text-[24px] font-medium text-[#0f2c6b]"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  S
                </span>
              </div>
              <div>
                <div
                  className="text-[20px] font-medium text-[#0f2c6b] tracking-wide"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  SAVARO INDIA
                </div>
                <div className="text-[9.5px] text-slate-500 tracking-[0.28em] font-medium uppercase">
                  Trademark Managed
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: Copyright */}
        <div className="py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] text-slate-500">
          <div className="tracking-wider">© 2026 Tropic Blue®. All Rights Reserved.</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-[#c4a24a] transition-colors">Privacy</a>
            <span className="text-slate-300">|</span>
            <a href="#" className="hover:text-[#c4a24a] transition-colors">Terms</a>
            <span className="text-slate-300">|</span>
            <a href="#" className="hover:text-[#c4a24a] transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
